import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { type MailboxInfo } from 'owa-client-types';
import {
    getOptionsForFeature,
    MonarchKeyboardShortcutsMode,
    OwsOptionsFeatureType,
    type KeyboardShortcutsOptions,
} from 'owa-outlook-service-option-store';
import {
    getOcpsPolicyNumericValue,
    isUserAllowedToChangeSettings,
    KeyboardStylesPolicySettingId,
    OcpsPolicyFeature,
} from 'owa-owsprime-policies-store';
import { type default as WebKeyboardShortcutsMode } from 'owa-service/lib/contract/KeyboardShortcutsMode';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import shouldUseMonarchShortcuts from './shouldUseMonarchShortcuts';

// KeyboardShortcutsMode is a const enum (no runtime object), so validate policy values against
// explicit members rather than an `in` check.
// The mapping below is based on the enum values from CPS admin policy schema
// Please check https://clients.config.office.net/settings/v1.0/settingscatalog/settings?idSubstring=L_WebKeyboardShortcutStyle to update the mapping.
const VALID_WEB_MODES = new Set<number>([0, 2, 3, 4, 1]);

// The mapping below is based on the enum values from CPS admin policy schema
// Please check https://clients.config.office.net/settings/v1.0/settingscatalog/settings?idSubstring=L_WindowsKeyboardShortcutStyle to update the mapping.
const VALID_MONARCH_MODES = new Set<number>([
    MonarchKeyboardShortcutsMode.OutlookForWindows,
    MonarchKeyboardShortcutsMode.OutlookForWeb,
    MonarchKeyboardShortcutsMode.Off,
]);

export default function getKeyboardShortcutsMode():
    | WebKeyboardShortcutsMode
    | MonarchKeyboardShortcutsMode {
    const useMonarch = shouldUseMonarchShortcuts();

    // OCPS admin policy: when policies are enabled and the admin disallows user override,
    // force the policy-configured shortcut style (Monarch -> Windows style, Web -> Web style).
    // An absent or out-of-range policy value falls through to the OutlookForWindows or Owa based on the platform.
    const globalMailboxInfo = getGlobalSettingsAccountMailboxInfo();
    if (!isUserAllowedToChangeSettings(OcpsPolicyFeature.KeyboardStyles, globalMailboxInfo)) {
        return getPolicyValueOrDefault(useMonarch, globalMailboxInfo);
    }

    // If the user is allowed to change settings, return the user's configured value.
    // If the user hasn't configured the shortcuts then choose admin set defaults if configured or fallback to platform specific default.
    const userConfiguredShortcutMode = useMonarch
        ? getOptionsForFeature<KeyboardShortcutsOptions>(OwsOptionsFeatureType.KeyboardShortcuts)
              .keyboardShortcutsMode
        : getUserConfiguration().UserOptions?.KeyboardShortcutsMode;

    return userConfiguredShortcutMode ?? getPolicyValueOrDefault(useMonarch, globalMailboxInfo);
}

function getPolicyValueOrDefault(
    useMonarch: boolean,
    mailboxInfo: MailboxInfo
): WebKeyboardShortcutsMode | MonarchKeyboardShortcutsMode {
    const settingId = useMonarch
        ? KeyboardStylesPolicySettingId.OutlookWindowsKeyboardShortcutStyle
        : KeyboardStylesPolicySettingId.OutlookWebKeyboardShortcutStyle;
    const policyValue = getOcpsPolicyNumericValue(settingId, mailboxInfo);
    if (useMonarch) {
        return policyValue != undefined && VALID_MONARCH_MODES.has(policyValue)
            ? (policyValue as MonarchKeyboardShortcutsMode)
            : MonarchKeyboardShortcutsMode.OutlookForWindows; // default to Windows style if policy is absent or invalid
    } else {
        return policyValue != undefined && VALID_WEB_MODES.has(policyValue)
            ? (policyValue as WebKeyboardShortcutsMode)
            : 4;
    }
}
